/**
 * CommandPalette — #2323 Cmd+K global search
 *
 * Searches across:
 *   - App routes (static list)
 *   - Hives (fetched from /admin/hives)
 *   - Fleet cards (fetched from /fleet/jobs/list, recent 200)
 *   - Agents (from /fleet/sessions/metadata via SessionMetaContext)
 *
 * Fuzzy match: case-insensitive substring match on label + sub-text.
 * Keyboard: ArrowUp/Down, Enter to activate, Escape to close.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { projectsApi } from "../api/projectsApi";
import { fleetApi }     from "../api/fleetApi";
import { useSessionMeta } from "../context/SessionMetaContext";

// ── Types ─────────────────────────────────────────────────────────────────────

type ItemKind = "route" | "hive" | "card" | "agent";

interface PaletteItem {
  id:       string;
  kind:     ItemKind;
  label:    string;
  sub?:     string;
  /** keyboard shortcut hint shown on the right */
  hint?:    string;
  action:   () => void;
}

// ── Static route index ────────────────────────────────────────────────────────

const ROUTES: Array<{ label: string; path: string; sub?: string; hint?: string }> = [
  { label: "Dashboard",      path: "/",              sub: "Home" },
  { label: "Hives",          path: "/hives",         sub: "All your hives" },
  { label: "Fleet Board",    path: "/fleet/board",   sub: "Kanban board", hint: "Board" },
  { label: "Integrations",   path: "/integrations",  sub: "GitHub, API keys, OAuth" },
  { label: "Profile",        path: "/profile",       sub: "Account settings" },
  { label: "Billing",        path: "/billing",       sub: "Subscription & invoices" },
  { label: "Usage",          path: "/usage",         sub: "Token usage stats" },
  { label: "Upgrade",        path: "/upgrade",       sub: "Plans & pricing" },
  { label: "Servers",        path: "/servers",       sub: "LXC environment" },
  { label: "Audit Log",      path: "/audit",         sub: "Event history" },
  { label: "SSH Key",        path: "/profile/ssh-key", sub: "Generate or rotate key" },
  { label: "Changelog",      path: "/changelog",     sub: "What's new" },
  { label: "Danger Zone",    path: "/danger-zone",   sub: "Account deletion & export" },
];

// ── Fuzzy match ───────────────────────────────────────────────────────────────

function fuzzyMatch(query: string, text: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  // Initialism match: "fb" matches "Fleet Board"
  const words = t.split(/\s+/);
  const initials = words.map((w) => w[0] ?? "").join("");
  if (initials.includes(q)) return true;
  return false;
}

function matchItem(query: string, item: PaletteItem): boolean {
  return (
    fuzzyMatch(query, item.label) ||
    (item.sub ? fuzzyMatch(query, item.sub) : false)
  );
}

// ── Kind label + colours ──────────────────────────────────────────────────────

const KIND_META: Record<ItemKind, { label: string; color: string }> = {
  route: { label: "page",  color: "var(--brand-text-muted)" },
  hive:  { label: "hive",  color: "var(--brand-status-success)" },
  card:  { label: "card",  color: "var(--brand-job-in-progress)" },
  agent: { label: "agent", color: "var(--brand-status-warning)" },
};

// ── Component ─────────────────────────────────────────────────────────────────

interface CommandPaletteProps {
  onClose: () => void;
}

export function CommandPalette({ onClose }: CommandPaletteProps) {
  const navigate  = useNavigate();
  const sessionMeta = useSessionMeta();

  const [query,   setQuery]   = useState("");
  const [hives,   setHives]   = useState<PaletteItem[]>([]);
  const [cards,   setCards]   = useState<PaletteItem[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLUListElement>(null);

  // Focus input when palette opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Fetch hives on mount
  useEffect(() => {
    projectsApi.list().then((ps) => {
      setHives(
        ps.map((p) => ({
          id:     `hive:${p.name}`,
          kind:   "hive" as ItemKind,
          label:  p.name,
          sub:    p.description || p.clone_status,
          action: () => { navigate(`/hives/${encodeURIComponent(p.name)}`); onClose(); },
        }))
      );
    }).catch(() => {});
  // onClose is stable; navigate is stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Fetch recent fleet cards on mount
  useEffect(() => {
    fleetApi.listJobs({ limit: 200 }).then((jobs) => {
      setCards(
        jobs.map((j) => ({
          id:     `card:${j.id}`,
          kind:   "card" as ItemKind,
          label:  `#${j.id} ${j.title}`,
          sub:    `${j.status} · ${j.target}`,
          action: () => {
            // Navigate to board with card pre-selected
            navigate(`/fleet/board?card=${j.id}`);
            onClose();
          },
        }))
      );
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Build agent items from SessionMetaContext
  const agentItems = useMemo<PaletteItem[]>(() => {
    const meta = sessionMeta?.sessionMeta;
    if (!meta) return [];
    return Object.entries(meta).map(([key, s]) => ({
      id:     `agent:${key}`,
      kind:   "agent" as ItemKind,
      label:  s.name ?? key,
      sub:    s.project ? `project: ${s.project}` : undefined,
      action: () => {
        if (s.project) navigate(`/hives/${encodeURIComponent(s.project)}?agent=${encodeURIComponent(s.name ?? key)}`);
        onClose();
      },
    }));
  }, [sessionMeta?.sessionMeta, navigate, onClose]);

  // Build route items
  const routeItems = useMemo<PaletteItem[]>(() =>
    ROUTES.map((r) => ({
      id:     `route:${r.path}`,
      kind:   "route" as ItemKind,
      label:  r.label,
      sub:    r.sub,
      hint:   r.hint,
      action: () => { navigate(r.path); onClose(); },
    })),
  [navigate, onClose]);

  // All items: routes first, then hives, cards, agents
  const allItems = useMemo<PaletteItem[]>(() => [
    ...routeItems,
    ...hives,
    ...cards,
    ...agentItems,
  ], [routeItems, hives, cards, agentItems]);

  const filtered = useMemo<PaletteItem[]>(() => {
    if (!query.trim()) return allItems.slice(0, 40);
    return allItems.filter((item) => matchItem(query, item)).slice(0, 40);
  }, [query, allItems]);

  // Reset active index when results change
  useEffect(() => { setActiveIdx(0); }, [filtered.length]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      filtered[activeIdx]?.action();
      return;
    }
  }, [onClose, filtered, activeIdx]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.65)", backdropFilter: "blur(2px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "80px 16px 16px",
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "var(--brand-bg-elevated)",
        border: "1px solid var(--brand-border-strong)",
        borderRadius: "var(--brand-radius-lg)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        width: "100%", maxWidth: 620,
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Search input */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 16px",
          borderBottom: "1px solid var(--brand-border-subtle)",
        }}>
          <span style={{ fontSize: "1rem", opacity: 0.5, flexShrink: 0 }}>⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search hives, cards, agents, pages…"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "var(--brand-text-primary)",
              fontSize: "var(--brand-text-md)",
              fontFamily: "var(--brand-font-sans)",
            }}
            aria-label="Command palette search"
            role="combobox"
            aria-expanded="true"
            aria-autocomplete="list"
            aria-controls="cmd-palette-list"
          />
          <kbd style={{
            fontSize: "0.65rem", color: "var(--brand-text-muted)",
            border: "1px solid var(--brand-border-subtle)",
            borderRadius: "var(--brand-radius-sm)", padding: "2px 5px",
            flexShrink: 0, fontFamily: "var(--brand-font-sans)",
          }}>esc</kbd>
        </div>

        {/* Results list */}
        <ul
          id="cmd-palette-list"
          ref={listRef}
          role="listbox"
          style={{
            margin: 0, padding: "6px 0", listStyle: "none",
            maxHeight: 400, overflowY: "auto",
          }}
        >
          {filtered.length === 0 && (
            <li style={{
              padding: "16px", textAlign: "center",
              color: "var(--brand-text-muted)", fontSize: "var(--brand-text-sm)",
            }}>
              No results for "{query}"
            </li>
          )}
          {filtered.map((item, idx) => {
            const { label: kindLabel, color: kindColor } = KIND_META[item.kind];
            const isActive = idx === activeIdx;
            return (
              <li
                key={item.id}
                role="option"
                aria-selected={isActive}
                data-testid={`cmd-palette-item-${item.id}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={item.action}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 16px", cursor: "pointer",
                  background: isActive ? "var(--brand-bg-overlay)" : "transparent",
                  transition: "background 0.1s ease",
                }}
              >
                {/* Kind badge */}
                <span style={{
                  fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: kindColor,
                  border: `1px solid ${kindColor}44`,
                  borderRadius: "var(--brand-radius-sm)", padding: "1px 5px",
                  flexShrink: 0, minWidth: 38, textAlign: "center",
                  fontFamily: "var(--brand-font-sans)",
                }}>
                  {kindLabel}
                </span>

                {/* Main text */}
                <span style={{
                  flex: 1, minWidth: 0,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  <span style={{
                    color: "var(--brand-text-primary)",
                    fontSize: "var(--brand-text-sm)",
                    fontFamily: "var(--brand-font-sans)",
                  }}>
                    {item.label}
                  </span>
                  {item.sub && (
                    <span style={{
                      color: "var(--brand-text-muted)",
                      fontSize: "var(--brand-text-xs)",
                      marginLeft: 8,
                      fontFamily: "var(--brand-font-sans)",
                    }}>
                      {item.sub}
                    </span>
                  )}
                </span>

                {/* Hint / shortcut */}
                {item.hint && (
                  <span style={{
                    fontSize: "0.65rem", color: "var(--brand-text-muted)",
                    flexShrink: 0, fontFamily: "var(--brand-font-sans)",
                  }}>
                    {item.hint}
                  </span>
                )}
                {isActive && (
                  <kbd style={{
                    fontSize: "0.6rem", color: "var(--brand-text-muted)",
                    border: "1px solid var(--brand-border-subtle)",
                    borderRadius: "var(--brand-radius-sm)", padding: "1px 4px",
                    flexShrink: 0, fontFamily: "var(--brand-font-sans)",
                  }}>
                    enter
                  </kbd>
                )}
              </li>
            );
          })}
        </ul>

        {/* Footer hint */}
        <div style={{
          padding: "7px 16px",
          borderTop: "1px solid var(--brand-border-subtle)",
          display: "flex", gap: 14, flexWrap: "wrap",
          fontSize: "0.62rem", color: "var(--brand-text-muted)",
          fontFamily: "var(--brand-font-sans)",
        }}>
          {[
            { key: "↑↓",     desc: "navigate" },
            { key: "enter",  desc: "open" },
            { key: "esc",    desc: "close" },
            { key: "⌘K",     desc: "toggle" },
          ].map(({ key, desc }) => (
            <span key={key}>
              <kbd style={{
                border: "1px solid var(--brand-border-subtle)",
                borderRadius: "3px", padding: "1px 4px",
                fontFamily: "var(--brand-font-sans)",
              }}>{key}</kbd>{" "}{desc}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
